import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { PropsWithChildren, useCallback, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import { ItemsList, type ItemsListComponentProps } from '@/components/itemsList/itemsList';
import {
    DataModelDependentPage,
    DeploymentInProgress,
    NoDataModelEmptyState,
} from '@/components/shared';
import { DialogHost } from '@/dialogs/DialogHost';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';

import { SearchFilterBar } from './components/SearchFilterBar';
import { useBrowseQuery } from './hooks/useBrowseQuery';
import { ItemsListMeta } from './types';

const noop = () => undefined;

/**
 * Placeholder callbacks until LAX-10137 wires the AngularJS bridge (and
 * later tickets replace these with React handlers). Safe: this page is
 * registered on `window.reactPages` but not routed yet.
 */
const DEFAULT_ITEMS_LIST_PROPS: ItemsListComponentProps = {
    selectAllItems: noop,
    clearSelectedItems: noop,
    handleSelectedItems: noop,
    isCatalogTable: false,
    loadWatchlistItems: noop,
};

/**
 * LAX-10128: React shell for `/browse`. Guards mirror `browse.tpl.html`
 * lines 1-22 (loader / first-time deploy / no data model / no browse
 * layout). LAX-10133 adds {@link SearchFilterBar} + {@link useBrowseQuery}.
 *
 * Not routed yet — `window.reactPages['BrowsePage']` is exposed via a lazy
 * getter in `main.tsx` only when `NEXT_HIT_LIST` is enabled (same gate as
 * the React `#items-list` mount in items-controller.js). Inert until
 * LAX-10137 flips the AngularJS route. `<ItemsList />` is unchanged and
 * still mounted by AngularJS today.
 *
 * `children` is a Storybook seam so empty-state stories can avoid
 * mounting the AngularJS-bridged items list.
 *
 * LAX-10130: wrapped in DialogHost so browse actions can open the existing
 * React dialogs without AngularJS `$uibModal`.
 */
const BrowsePage = ({ children }: PropsWithChildren) => {
    const { t } = useTranslation();
    const { organization } = useOrganization();
    const { dataModel } = useDataModel();
    const {
        query,
        setQuery,
        activeFilters,
        setActiveFilter,
        removeFilter,
        applyBulkSearch,
    } = useBrowseQuery();
    const [itemsMeta, setItemsMeta] = useState<ItemsListMeta | null>(null);

    const handleKeywordChange = useCallback(
        (keyword: string) => {
            setQuery({ keyword });
        },
        [setQuery],
    );

    const handleItemsMetaChange = useCallback((meta: ItemsListMeta) => {
        setItemsMeta(meta);
    }, []);

    // Mirror `application-controller.js#blockDuringDataModelDeployment`.
    // Checked before the loader so a first-time deploy is not masked by
    // a spinner (`browse.tpl.html` lines 3-5).
    const isBlockingDeployment =
        !!organization?.isDataModelDeploying && !organization?.dataModelHash;
    if (isBlockingDeployment) {
        return (
            <DialogHost>
                <DeploymentInProgress />
            </DialogHost>
        );
    }

    // `DataModelProvider` already renders a Loader until `dataModel` is
    // ready. This branch is for Storybook (which can provide the context
    // with `dataModel: undefined`) and for the day the provider stops
    // blocking children during load.
    if (!dataModel) {
        return (
            <DialogHost>
                <div
                    role="status"
                    aria-label="loading"
                    className="tw-flex tw-items-center tw-justify-center tw-pt-52"
                >
                    <Loader size="lg" />
                </div>
            </DialogHost>
        );
    }

    // Mirror `browse.tpl.html` line 7: data model uploaded but it has no
    // browse layout. Same empty state as "no data model chosen yet".
    if (organization?.dataModelHash && !dataModel.hasLayout('browse')) {
        return (
            <DialogHost>
                <NoDataModelEmptyState />
            </DialogHost>
        );
    }

    return (
        <DialogHost>
            <DataModelDependentPage>
                <Panel>
                    <PanelHeader
                        icon={ClipboardDocumentListIcon}
                        title={t('ITEMS')}
                    />
                    <PanelBody className="tw-h-[calc(100vh-120px)]">
                        {children ?? (
                            <>
                                <SearchFilterBar
                                    keyword={query.keyword ?? ''}
                                    onKeywordChange={handleKeywordChange}
                                    activeFilters={activeFilters}
                                    onSetActiveFilter={setActiveFilter}
                                    onRemoveFilter={removeFilter}
                                    onApplyBulkSearch={applyBulkSearch}
                                    itemsMeta={itemsMeta}
                                />
                                <ItemsList
                                    {...DEFAULT_ITEMS_LIST_PROPS}
                                    query={query}
                                    hideFoundItemsLabel
                                    onItemsMetaChange={handleItemsMetaChange}
                                />
                            </>
                        )}
                    </PanelBody>
                </Panel>
            </DataModelDependentPage>
        </DialogHost>
    );
};

BrowsePage.displayName = 'BrowsePage';

export { BrowsePage };
